import { observable, action, runInAction } from "mobx";
import agent from "../agent";
import CacheHelper from "../helper/cache";

class HgSuperPayAddonStore {
    @observable addonStatus = "loading";
    @observable currentMonthUsage = 0;
    @observable previousMonthUsage = 0;
    @observable temporaryFeeAmountFormatted = null;
    @observable currency = null;
    @observable isActionLoading = false;

    get isActive() {
        return this.addonStatus === "active";
    }

    @action
    async loadAddonStatusForProfile(profileId) {
        if (!profileId) return;

        // Sequence counter: results from a previous call are discarded if a newer
        // call has started (e.g. user switching venues rapidly).
        this._addonLoadSeq = (this._addonLoadSeq || 0) + 1;
        const seq = this._addonLoadSeq;

        this.addonStatus = "loading";
        try {
            const response = await agent.HgSuperPayAddon.getStatus(profileId);
            if (this._addonLoadSeq !== seq) return; // stale — newer load has started
            runInAction(() => {
                this.addonStatus = response?.subscribed ? "active" : "inactive";
                this.currentMonthUsage = response?.currentMonthUsage || 0;
                this.previousMonthUsage = response?.previousMonthUsage || 0;
                this.temporaryFeeAmountFormatted = response?.temporaryFeeAmountFormatted || null;
                this.currency = response?.currency || null;
            });
        } catch (e) {
            if (this._addonLoadSeq !== seq) return; // stale
            runInAction(() => {
                this.addonStatus = "inactive";
                this.currentMonthUsage = 0;
                this.previousMonthUsage = 0;
                this.temporaryFeeAmountFormatted = null;
                this.currency = null;
            });
        }
    }

    @action
    async loadAddonStatus() {
        const currentProfile = CacheHelper.getCurrentProfile();
        if (!currentProfile?.id) return;

        this.addonStatus = "loading";
        try {
            const response = await agent.HgSuperPayAddon.getStatus(currentProfile.id);
            runInAction(() => {
                this.addonStatus = response?.subscribed ? "active" : "inactive";
                this.currentMonthUsage = response?.currentMonthUsage || 0;
                this.previousMonthUsage = response?.previousMonthUsage || 0;
                this.temporaryFeeAmountFormatted = response?.temporaryFeeAmountFormatted || null;
                this.currency = response?.currency || null;
            });
        } catch (e) {
            runInAction(() => {
                this.addonStatus = "inactive";
                this.currentMonthUsage = 0;
                this.previousMonthUsage = 0;
                this.temporaryFeeAmountFormatted = null;
                this.currency = null;
            });
        }
    }

    @action
    async startCheckoutForProfile(profileId, isOrganisation = false) {
        if (!profileId) return;

        this.isActionLoading = true;
        try {
            const response = await agent.HgSuperPayAddon.subscribe(profileId, isOrganisation);
            runInAction(() => {
                this.isActionLoading = false;
            });
            return response;
        } catch (e) {
            runInAction(() => {
                this.isActionLoading = false;
            });
            const status = e?.response?.statusCode || e?.response?.status;
            const err = new Error(e?.message || "Unknown error");
            err.status = status;
            throw err;
        }
    }

    @action
    async startCheckout() {
        const currentProfile = CacheHelper.getCurrentProfile();
        if (!currentProfile?.id) return;

        this.isActionLoading = true;
        try {
            const response = await agent.HgSuperPayAddon.subscribe(currentProfile.id);
            runInAction(() => {
                this.isActionLoading = false;
            });
            return response;
        } catch (e) {
            runInAction(() => {
                this.isActionLoading = false;
            });
            const status = e?.response?.statusCode || e?.response?.status;
            const err = new Error(e?.message || "Unknown error");
            err.status = status;
            throw err;
        }
    }

    @action
    async cancelAddon() {
        const currentProfile = CacheHelper.getCurrentProfile();
        if (!currentProfile?.id) return;

        this.isActionLoading = true;
        try {
            const response = await agent.HgSuperPayAddon.cancel(currentProfile.id);
            runInAction(() => {
                this.addonStatus = "inactive";
                this.currentMonthUsage = 0;
                this.previousMonthUsage = 0;
                this.temporaryFeeAmountFormatted = null;
                this.currency = null;
                this.isActionLoading = false;
            });
            return response;
        } catch (e) {
            runInAction(() => {
                this.isActionLoading = false;
            });
            const status = e?.response?.statusCode || e?.response?.status;
            const err = new Error(e?.message || "Unknown error");
            err.status = status;
            throw err;
        }
    }
}

export default new HgSuperPayAddonStore();
